import React, { Component } from 'react';
import PropTypes from 'prop-types';
import apiRequest from '../utils/apiRequest';
import { blink } from '../utils/animations';
import findIdByPath from '../utils/findIdByPath';
import styled, { css } from 'styled-components';
import {
  SecondaryTitle,
  Card,
  CardSpacing,
  CardContent,
  CardAction,
  CardActionIcon,
  Edit
} from './CommonUiComponents';
import ProgressBar from './ProgressBar';
import StatusLoader from './StatusLoader';
import { cross, editWhite, plus } from '../utils/icons';
import { textGray } from '../utils/colors';
import ErrorBoundary from './ErrorBoundary';

const subGoalType = PropTypes.shape({
  title: PropTypes.string.isRequired,
  _id: PropTypes.string.isRequired,
  done: PropTypes.bool.isRequired
});

const goalType = PropTypes.shape({
  _id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  subTasks: PropTypes.arrayOf(subGoalType).isRequired,
  done: PropTypes.bool.isRequired,
  percentageDone: PropTypes.number.isRequired
});

const GoalWrapper = CardAction.extend`
  padding: 0.5rem 2rem;
  border-radius: 0.625rem;
  position: relative;
  margin-left: 1.5rem;
  margin-right: 1.5rem;
  font-size: 1rem;
  transition: opacity 0.2s ease-in-out;
  background-color: rgba(255, 255, 255, 0.3);

  &:hover {
    cursor: inherit;
    background-color: rgba(255, 255, 255, 0.3);
  }

  ${props =>
    props.busy &&
    css`
      cursor: wait;
      animation: ${blink} 0.8s infinite ease-in-out;
    `};
`;

const GoalInner = styled.div`
  margin: 1rem 0;
`;

const GoalInput = styled.input`
  appearance: none;
  width: 25px;
  height: 25px;
  margin: 0 1.125rem 0 0;
  background-color: #ffffff;
  vertical-align: middle;
  border-radius: 4px;
  position: relative;
  cursor: pointer;
  flex-shrink: 0;

  ~ svg {
    cursor: pointer;
  }

  &:checked {
    ~ svg {
      display: inline-block;
      height: 18px;
      width: 18px;
      position: absolute;
      left: 4px;
      top: 4px;
    }
  }

  ${props =>
    props.subGoal &&
    css`
      width: 20px;
      height: 20px;

      &:checked {
        ~ svg {
          display: inline-block;
          height: 13px;
          width: 13px;
          position: absolute;
          left: 4px;
          top: 3px;
        }
      }
    `};
`;

const GoalLabel = styled.label`
  display: flex;
  margin-bottom: 1rem;
  position: relative;
  align-items: center;
  cursor: pointer;
  color: ${textGray};

  ${props =>
    props.inverted &&
    css`
      color: #ffffff;
    `};

  ${props =>
    props.subGoal &&
    css`
      margin-left: 1rem;
      font-weight: 300;
    `};

  ${props =>
    props.done &&
    css`
      span {
        text-decoration: line-through;
      }
    `};

  svg {
    display: none;

    g {
      stroke: ${props => props.theme.tertiary};
    }
  }
`;

const SubGoalNewInput = styled.input`
  background: transparent;
  font-size: 1rem;
  font-weight: 600;
  border: 0;
  color: ${textGray};
  outline: none;
  margin-left: 1rem;
  transition: color 0.2s ease-in-out;

  ${props =>
    props.disabled &&
    css`
      color: rgba(255, 255, 255, 0.3);
      pointer-events: none;
    `};

  &::placeholder {
    font-size: 1rem;
    color: rgba(0, 0, 0, 0.3);
    transition: color 0.2s ease-in-out;
  }

  &:focus {
    &::placeholder {
      color: ${textGray};
    }
  }

  ${props =>
    props.inverted &&
    css`
      color: rgb(255, 255, 255);

      &::placeholder {
        color: rgba(255, 255, 255, 0.4);
      }

      &:focus {
        &::placeholder {
          color: rgb(255, 255, 255);
        }
      }
    `};
`;

const AddNewGoalForm = styled.form`
  margin: 0;
`;

const AddNewGoalInput = styled.input`
  background: transparent;
  font-size: 1em;
  font-weight: 600;
  border: 0;
  color: ${textGray};
  outline: none;
  width: 100%;
  transition: color 0.2s ease-in-out;

  ${props =>
    props.disabled &&
    css`
      color: rgba(0, 0, 0, 0.2);
      pointer-events: none;
    `};

  &::placeholder {
    color: rgba(0, 0, 0, 0.3);
  }

  ${props =>
    props.inverted &&
    css`
      color: rgb(255, 255, 255);

      ${props =>
        props.disabled &&
        css`
          color: rgba(255, 255, 255, 0.3);
        `};

      &::placeholder {
        color: rgba(255, 255, 255, 0.4);
      }
    `};
`;

const CancelButton = styled.a`
  margin-top: 0.5rem;
  cursor: pointer;
  display: inline-block;
  color: ${props => props.theme.tertiary};
  text-transform: uppercase;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const Delete = styled.a`
  margin-left: auto;
  text-transform: uppercase;
  font-weight: 600;
  transition: color 0.2s ease-in-out;
  color: ${textGray};
  text-decoration: none;

  ${props =>
    props.inverted &&
    css`
      color: #ffffff;
    `};

  &:hover {
    color: #ff3838;
  }
`;

const EmptyState = styled.div`
  padding: 0 3.5rem;
`;

class Goal extends Component {
  state = {
    subTaskValue: ''
  };

  addSubtask = async e => {
    const { g, fetchGoals, posting, postGoalAction } = this.props;
    const { subTaskValue } = this.state;

    e.preventDefault();
    if (posting === g._id) return false;
    postGoalAction(g._id);

    await apiRequest('progresser', `tasks/${g._id}/sub-tasks`, 'post', {
      title: subTaskValue
    });

    fetchGoals(() =>
      this.setState({
        subTaskValue: ''
      })
    );
  };

  deleteTask = async (subTask, id) => {
    const { g, fetchGoals, posting, postGoalAction } = this.props;
    if (posting === g._id) return false;
    postGoalAction(g._id);

    const deleteAction = subTask
      ? `tasks/${g._id}/sub-tasks/${id}`
      : `tasks/${id}`;

    await apiRequest('progresser', deleteAction, 'delete');
    fetchGoals();
  };

  render() {
    const {
      g,
      toggleGoalCompleted,
      toggleSubGoalCompleted,
      posting,
      editMode,
      inverted
    } = this.props;

    const { subTaskValue } = this.state;

    return (
      <GoalWrapper busy={posting === g._id}>
        <GoalInner>
          <GoalLabel
            inverted={inverted}
            done={g.percentageDone === 100 || g.done}
          >
            <GoalInput
              type="checkbox"
              value={g._id}
              onChange={e => toggleGoalCompleted(e, g._id, g.done)}
              checked={g.percentageDone === 100 || g.done}
            />
            {cross}
            <span>{g.title}</span>
            {editMode && (
              <Delete
                href="#"
                inverted={inverted}
                onClick={e => {
                  e.preventDefault();
                  this.deleteTask(false, g._id);
                  e.stopPropagation();
                }}
              >
                Slett
              </Delete>
            )}
          </GoalLabel>
        </GoalInner>
        <GoalInner>
          {g.subTasks.map(s => {
            return (
              <GoalLabel inverted={inverted} subGoal key={s._id} done={s.done}>
                <GoalInput
                  subGoal
                  type="checkbox"
                  value={s._id}
                  onChange={e =>
                    toggleSubGoalCompleted(e, g._id, s._id, s.done)
                  }
                  checked={s.done}
                />
                {cross}
                <span>{s.title}</span>
                {editMode && (
                  <Delete
                    href="#"
                    inverted={inverted}
                    onClick={e => {
                      e.preventDefault();
                      this.deleteTask(true, s._id);
                      e.stopPropagation();
                    }}
                  >
                    Slett
                  </Delete>
                )}
              </GoalLabel>
            );
          })}
        </GoalInner>
        <GoalInner>
          <form onSubmit={this.addSubtask}>
            <SubGoalNewInput
              disabled={posting === g._id}
              type="text"
              onChange={e => this.setState({ subTaskValue: e.target.value })}
              placeholder="Legg til delmål…"
              value={subTaskValue}
              inverted={inverted}
            />
          </form>
        </GoalInner>
        <GoalInner>
          <ProgressBar
            inverted={inverted}
            nonAbsolute
            progress={Math.round(g.percentageDone)}
          />
        </GoalInner>
      </GoalWrapper>
    );
  }
}

Goal.propTypes = {
  g: goalType.isRequired,
  handleNewGoalTitle: PropTypes.func.isRequired,
  toggleGoalCompleted: PropTypes.func.isRequired,
  toggleSubGoalCompleted: PropTypes.func.isRequired,
  fetchGoals: PropTypes.func.isRequired,
  postGoalAction: PropTypes.func.isRequired,
  posting: PropTypes.string,
  editMode: PropTypes.bool.isRequired,
  inverted: PropTypes.bool.isRequired
};

const RenderGoals = props => {
  return props.goals.map(g => {
    return <Goal key={g._id} g={g} {...props} />;
  });
};

class Goals extends Component {
  state = {
    addingGoal: false,
    postingGoal: false,
    newGoalTitle: '',
    newSubGoalTitle: '',
    postingSubGoal: false,
    editMode: false
  };

  componentDidMount() {
    const { fetchGoals } = this.props;
    fetchGoals();
  }

  componentDidUpdate(prevProps, prevState) {
    const { addingGoal } = this.state;

    if (addingGoal && !prevState.addingGoal) {
      this.input.focus();
    }
  }

  startAddingGoal = () => {
    this.setState({ addingGoal: true });
  };

  cancelAddingGoal = () => {
    this.setState({
      addingGoal: false,
      newGoalTitle: ''
    });
  };

  addGoal = async e => {
    const { fetchGoals } = this.props;
    const { postingGoal, newGoalTitle } = this.state;

    e.preventDefault();

    if (postingGoal) return false;

    this.setState({
      postingGoal: true
    });

    await apiRequest('progresser', 'tasks', 'post', {
      title: newGoalTitle,
      type: findIdByPath()
    });

    fetchGoals(() =>
      this.setState({
        postingGoal: false,
        newGoalTitle: '',
        addingGoal: false
      })
    );
  };

  addSubGoal = async (e, id) => {
    const { fetchGoals } = this.props;
    const { newSubGoalTitle } = this.state;

    e.preventDefault();

    this.setState({ postingSubGoal: true });

    await apiRequest('progresser', `tasks/${id}/sub-tasks`, 'post', {
      title: newSubGoalTitle,
      type: 'this-book'
    });

    this.setState({
      postingSubGoal: false,
      newSubGoalTitle: ''
    });

    fetchGoals();
  };

  toggleGoalCompleted = async (e, id, done) => {
    const { fetchGoals, postGoalAction } = this.props;

    postGoalAction(id);

    if (!done) {
      await apiRequest('progresser', `tasks/${id}/set-done`, 'post');
      fetchGoals();
      return;
    }

    await apiRequest('progresser', `tasks/${id}/set-undone`, 'post');
    fetchGoals();
  };

  toggleSubGoalCompleted = async (e, taskId, subTaskId, done) => {
    const { fetchGoals, postGoalAction } = this.props;

    postGoalAction(taskId);

    if (!done) {
      await apiRequest(
        'progresser',
        `tasks/${taskId}/sub-tasks/${subTaskId}/set-done`,
        'post'
      );
      fetchGoals();
      return;
    }

    await apiRequest(
      'progresser',
      `tasks/${taskId}/sub-tasks/${subTaskId}/set-undone`,
      'post'
    );

    fetchGoals();
  };

  render() {
    const {
      goals,
      fetchGoals,
      posting,
      postGoalAction,
      invertedTheme
    } = this.props;
    const { addingGoal, editMode, postingGoal } = this.state;

    return (
      <Card>
        <ErrorBoundary>
          <CardSpacing>
            <CardContent>
              <SecondaryTitle inverted={invertedTheme}>
                Mine mål{' '}
                <Edit
                  href="#"
                  inverted={invertedTheme}
                  onClick={e => {
                    e.preventDefault();
                    this.setState({ editMode: !editMode });
                  }}
                >
                  {editMode ? cross : editWhite}
                </Edit>
              </SecondaryTitle>
            </CardContent>
            <StatusLoader busy={false /* Need to get this right*/} center>
              {goals.length > 0 ? (
                <RenderGoals
                  inverted={invertedTheme}
                  goals={goals}
                  handleNewGoalTitle={e =>
                    this.setState({ newSubGoalTitle: e.target.value })
                  }
                  toggleGoalCompleted={this.toggleGoalCompleted}
                  toggleSubGoalCompleted={this.toggleSubGoalCompleted}
                  fetchGoals={fetchGoals}
                  posting={posting}
                  postGoalAction={postGoalAction}
                  editMode={editMode}
                />
              ) : (
                <EmptyState>Du har ikke satt deg noen mål enda!</EmptyState>
              )}
            </StatusLoader>
          </CardSpacing>
          <CardAction
            inverted={invertedTheme}
            onClick={e => {
              e.preventDefault();

              if (!addingGoal) this.startAddingGoal();
            }}
            busy={postingGoal}
            href="#"
          >
            <CardActionIcon inverted={invertedTheme}>{plus}</CardActionIcon>
            {addingGoal ? (
              <AddNewGoalForm onSubmit={this.addGoal}>
                <AddNewGoalInput
                  innerRef={comp => {
                    this.input = comp;
                  }}
                  type="text"
                  placeholder="Mitt mål..."
                  onChange={e =>
                    this.setState({ newGoalTitle: e.target.value })
                  }
                  inverted={invertedTheme}
                  goals={goals}
                  handleNewGoalTitle={e =>
                    this.setState({ newSubGoalTitle: e.target.value })
                  }
                  toggleGoalCompleted={this.toggleGoalCompleted}
                  toggleSubGoalCompleted={this.toggleSubGoalCompleted}
                  fetchGoals={fetchGoals}
                  posting={posting}
                  postGoalAction={postGoalAction}
                  editMode={editMode}
                />
                <CancelButton
                  href="#"
                  onClick={e => {
                    e.preventDefault();
                    this.cancelAddingGoal();
                  }}
                >
                  Avbryt
                </CancelButton>
              </AddNewGoalForm>
            ) : (
              'Legg til mål'
            )}
          </CardAction>
        </ErrorBoundary>
      </Card>
    );
  }
}

Goals.propTypes = {
  fetching: PropTypes.bool.isRequired,
  goals: PropTypes.arrayOf(goalType.isRequired).isRequired,
  fetchGoals: PropTypes.func.isRequired,
  posting: PropTypes.string,
  postGoalAction: PropTypes.func.isRequired,
  donePostingGoalAction: PropTypes.func.isRequired
};

export default Goals;



// WEBPACK FOOTER //
// ./src/components/Goals.js